import { CelebrationTypeEnum, MemberEvents } from '@kudos/http-client'
import { format } from 'date-fns'

/**
 * The subset of {@link CelebrationTypeEnum} that maps to a member's recurring
 * milestone events (the ones backed by `MemberEvents` ranges). Declared as a
 * runtime constant plus matching type so it can be used both as a value
 * (`MemberCelebration.BIRTHDAY`, `Object.values(MemberCelebration)`) and as a
 * type, while staying assignable to `CelebrationTypeEnum`.
 */
export const MemberCelebration = {
  BIRTHDAY: CelebrationTypeEnum.BIRTHDAY,
  ANNIVERSARY: CelebrationTypeEnum.ANNIVERSARY,
  WELCOME: CelebrationTypeEnum.WELCOME,
} as const

export type MemberCelebration = (typeof MemberCelebration)[keyof typeof MemberCelebration]

const CELEBRATION_RANGE: Record<
  MemberCelebration,
  keyof Pick<MemberEvents, 'birthdayRange' | 'anniversaryRange' | 'welcomeRange'>
> = {
  [MemberCelebration.BIRTHDAY]: 'birthdayRange',
  [MemberCelebration.ANNIVERSARY]: 'anniversaryRange',
  [MemberCelebration.WELCOME]: 'welcomeRange',
}

/** Whether `todayKey` falls within the inclusive `[start, end]` date-only range. */
export const isWithinRange = (
  todayKey: string,
  range: [string, string] | null | undefined,
): boolean => {
  if (!range) return false
  const [start, end] = range
  return todayKey >= start && todayKey <= end
}

/**
 * Whether the range exists and has not ended yet — inside it, or still upcoming.
 * Use it to label a milestone; use `isWithinRange` to decide whether to celebrate it.
 */
export const isBeforeRangeEnd = (
  todayKey: string,
  range: [string, string] | null | undefined,
): boolean => {
  if (!range) return false
  const [, end] = range
  return todayKey <= end
}

/**
 * Resolves the member's active celebration for today, based on their own
 * browser-local date. Birthday takes priority over welcome, then anniversary,
 * when more than one is active on the same day. The welcome window owns the
 * work-milestone slot and blocks anniversary while active. Returns null when
 * no celebration is active.
 *
 * The nominal `birthday` and `anniversary` dates are projected onto the current
 * (or next) year rather than the year the event first happened, so we compare
 * today against the matching highlight range the contract provides instead of the
 * date itself; `welcome` is the real start date. Every date and range is withheld
 * unless the member has made that celebration public — their own record included.
 * Date-only string comparison in `yyyy-MM-dd` is timezone-safe.
 */
export const getActiveMemberCelebration = (
  events: MemberEvents | null | undefined,
  today: Date = new Date(),
): MemberCelebration | null => {
  if (!events) return null
  const todayKey = format(today, 'yyyy-MM-dd')
  if (isWithinRange(todayKey, events.birthdayRange)) return MemberCelebration.BIRTHDAY
  if (isWithinRange(todayKey, events.welcomeRange)) return MemberCelebration.WELCOME
  if (isWithinRange(todayKey, events.anniversaryRange)) return MemberCelebration.ANNIVERSARY
  return null
}

/**
 * Like getActiveMemberCelebration but also requires the matching public flag
 * to be true. Use on other-member profile surfaces where privacy must be
 * respected client-side.
 */
export const getActivePublicMemberCelebration = (
  events: MemberEvents | null | undefined,
  today: Date = new Date(),
): MemberCelebration | null => {
  if (!events) return null
  const todayKey = format(today, 'yyyy-MM-dd')
  if (isWithinRange(todayKey, events.birthdayRange) && events.birthdayPublic) {
    return MemberCelebration.BIRTHDAY
  }
  const welcomeActive = !!events.welcome && isWithinRange(todayKey, events.welcomeRange)
  if (welcomeActive) {
    return events.welcomePublic ? MemberCelebration.WELCOME : null
  }
  if (isWithinRange(todayKey, events.anniversaryRange) && events.anniversaryPublic) {
    return MemberCelebration.ANNIVERSARY
  }
  return null
}

/**
 * Whether the celebratory header treatment — background image plus particles —
 * belongs on the member's profile card or home widget today.
 *
 * A welcome does not qualify. Its highlight range runs from two weeks before the
 * start date through the 90th day after it, so honouring it would hold the
 * treatment up for a new hire's entire first quarter, while the treatment is
 * meant to mark a milestone day. A welcome still drives the avatar adornment,
 * the celebration banners, and the profile details rows.
 */
export const isCelebrationBackgroundActive = (
  events: MemberEvents | null | undefined,
  today: Date = new Date(),
): boolean => {
  const celebration = getActivePublicMemberCelebration(events, today)
  return celebration === MemberCelebration.BIRTHDAY || celebration === MemberCelebration.ANNIVERSARY
}

/** All celebrations active today (independent per-row check). */
export const getActiveMemberCelebrations = (
  events: MemberEvents | null | undefined,
  today: Date = new Date(),
): MemberCelebration[] => {
  if (!events) return []
  const todayKey = format(today, 'yyyy-MM-dd')
  return (Object.values(MemberCelebration) as MemberCelebration[]).filter(celebration =>
    isWithinRange(todayKey, events[CELEBRATION_RANGE[celebration]]),
  )
}

/** Whether a specific celebration type is active today (independent per-row check). */
export const isCelebrationActive = (
  events: MemberEvents | null | undefined,
  celebration: MemberCelebration,
  today: Date = new Date(),
): boolean => {
  if (!events) return false
  const todayKey = format(today, 'yyyy-MM-dd')
  return isWithinRange(todayKey, events[CELEBRATION_RANGE[celebration]])
}

const CELEBRATION_PUBLIC: Record<
  MemberCelebration,
  keyof Pick<MemberEvents, 'birthdayPublic' | 'anniversaryPublic' | 'welcomePublic'>
> = {
  [MemberCelebration.BIRTHDAY]: 'birthdayPublic',
  [MemberCelebration.ANNIVERSARY]: 'anniversaryPublic',
  [MemberCelebration.WELCOME]: 'welcomePublic',
}

/**
 * Like isCelebrationActive but also requires the celebration's public flag to
 * be true. Use anywhere the celebratory treatment (accent, chip, background)
 * must respect the member's privacy settings — including on their own profile —
 * so a private celebration is never highlighted.
 */
export const isPublicCelebrationActive = (
  events: MemberEvents | null | undefined,
  celebration: MemberCelebration,
  today: Date = new Date(),
): boolean =>
  isCelebrationActive(events, celebration, today) && !!events?.[CELEBRATION_PUBLIC[celebration]]
